import tinycolor from 'tinycolor2';
import { get, set } from './local-storage';

// Type definitions
export interface RGBAColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type ColorInput = string | RGBAColor | tinycolor.Instance;
export type ColorString = string;

/**
 * Get quick colors from local storage
 * @returns Array of color strings stored as quick colors
 */
export const getQuickColors = (): ColorString[] => {
  return get('apc_quick_colors', []);
};

/**
 * Add a color to the quick colors list in local storage
 * @param color - Color input (string, RGBA object, or tinycolor instance)
 */
export const addQuickColor = (color: ColorInput): void => {
  const colors: ColorString[] = getQuickColors();
  const alreadyExists = colors.some((item: ColorString) =>
    tinycolor.equals(item, color)
  );

  if (alreadyExists) return;

  const newColors: ColorString[] = [
    tinycolor(color).toRgbString(),
    ...colors.slice(0, 5),
  ];
  set('apc_quick_colors', newColors);
};

/**
 * Generate RGBA color string from color components
 * @param color - Object with r, g, b, a values
 * @returns RGBA color string
 */
export const generateColor = ({ r, g, b, a }: RGBAColor): ColorString =>
  `rgba(${r},${g},${b},${a})`;

/**
 * Check if a hex color string is valid (excluding hex3, hex4, and hex8 formats)
 * @param hex - Hex color string to validate
 * @returns True if valid hex color
 */
export const isValidHex = (hex: string) => {
  const hasHash = String(hex)[0] === '#';
  const expectedLength = hasHash ? 7 : 6; // #RRGGBB or RRGGBB
  return hex.length === expectedLength && tinycolor(hex).isValid();
};

/**
 * Check if a value is a CSS variable
 * @param value - Value to check
 * @returns True if the value contains CSS variable syntax
 */
export const isCSSVariable = (value: unknown): value is string =>
  value?.toString()?.includes('var(--') ?? false;
